import React, { useCallback } from 'react';
import { Button, Input, Space, TableProps, message, Switch, Modal } from 'antd';
import type { SorterResult, FilterValue } from 'antd/es/table/interface';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { TStateItem, IStateManagementInput } from 'types/operation';
import { getTextVisitStatePageList, saveOrUpdateTextVisitState } from 'api/operation';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';

const StateManagement = () => {
    // State Management tab state
    const [stateList, setStateList] = React.useState<TStateItem[]>([]);
    const [stateTotal, setStateTotal] = React.useState<number>(0);
    const [stateLoading, setStateLoading] = React.useState(false);
    const [statePage, setStatePage] = React.useState(1);
    const [stateSearchFilter, setStateSearchFilter] = React.useState<string>('');
    const [stateStatusFilter, setStateStatusFilter] = React.useState<string>('');
    const [stateOrderType, setStateOrderType] = React.useState<string>('DESC');
    const [stateOrderKey, setStateOrderKey] = React.useState<string>('LAST_MODIFIED');
    // Frontend display page size
    const pageSize = 10;
    // Backend request page size (fetch once and paginate on frontend)
    const requestPageSize = 60;

    // Modal states
    const [isModalVisible, setIsModalVisible] = React.useState(false);
    const [modalType, setModalType] = React.useState<'enable' | 'disable'>('enable');
    const [selectedRecord, setSelectedRecord] = React.useState<TStateItem | null>(null);

    // 直接使用 useSettings 获取州列表数据
    const [settings, settingsLoading] = useSettings();
    const stateOptions = React.useMemo(() => settings[EnumFields.STATE] || [], [settings]);

    // Hold full dataset for frontend pagination
    const [allStateList, setAllStateList] = React.useState<TStateItem[]>([]);

    // State Management data fetch function
    const doStateFetch = useCallback(async ({
        state,
        status,
    }: IStateManagementInput) => {
        setStateLoading(true);

        try {
            // Always fetch the full set; status filtering is done on the frontend
            const enabled: boolean | undefined = undefined;

            const params = {
                // Fetch all and filter on frontend; ignore search term in request
                state: '',
                enabled,
                // Fetch once with a large page size; paginate on frontend
                pageNum: 1,
                pageSize: requestPageSize,
            };

            const { data } = await getTextVisitStatePageList(params);
            // 根据API返回的数据结构，从 data.records 获取数据
            const rawList = (data?.data?.records ?? []) as Array<{ id?: string | number; state: string; enabled: boolean; sort?: number; lastModified?: string; updatedAt?: string }>;
            const mapped: TStateItem[] = (rawList || []).map((item, idx) => {
                // 从 stateOptions 中查找对应的州名称
                const stateObj = stateOptions.find((stateOption) => stateOption.value === item.state);
                const stateLabel = stateObj?.label || item.state;
                return {
                    id: String(item?.id ?? `${item.state}-${idx}`),
                    state: stateLabel,
                    status: item.enabled ? 'ACTIVE' : 'INACTIVE',
                    lastModified: item?.lastModified || item?.updatedAt || '',
                };
            });
            // Store full list for frontend pagination; total will be derived after filtering
            setAllStateList(mapped);
        } catch (e) {
            setStateList([]);
            setStateTotal(0);
        } finally {
            setStateLoading(false);
        }
    }, [requestPageSize, stateOptions]);

    // State Management table change handler
    const onStateTableChange: TableProps<TStateItem>['onChange'] = (
        pagination,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TStateItem> | SorterResult<TStateItem>[],
    ) => {
        const pageInx = pagination?.current || 1;
        setStatePage(pageInx);
        if (filters.status && Array.isArray(filters.status) && filters.status.length === 1) {
            setStateStatusFilter(filters.status[0] === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE');
        } else {
            setStateStatusFilter('');
        }
        if (sorter && !Array.isArray(sorter) && sorter.order) {
            const orderType =
              sorter.order === 'ascend' ? 'ASC' : 'DESC';
            const orderKey =
              sorter.field === 'lastModified' ? 'LAST_MODIFIED' : (sorter.field as string || 'LAST_MODIFIED');
            setStateOrderType(orderType);
            setStateOrderKey(orderKey);
        }
    };

    // State status change handler - 显示确认弹窗
    const handleStateStatusChange = (record: TStateItem) => {
        const newStatus = record.status !== 'ACTIVE';
        setSelectedRecord(record);
        setModalType(newStatus ? 'enable' : 'disable');
        setIsModalVisible(true);
    };

    // 确认状态变更
    const confirmStatusChange = async () => {
        if (!selectedRecord) return;

        try {
            const newEnabled = selectedRecord.status !== 'ACTIVE';
            const payload = {
                id: selectedRecord.id ? selectedRecord.id : null,
                enabled: newEnabled,
            };
            const res = await saveOrUpdateTextVisitState(payload);
            if (!res?.error) {
                message.success('Status updated successfully');
                // Reset to first page when data set changes
                setStatePage(1);
                doStateFetch({});
                setIsModalVisible(false);
                setSelectedRecord(null);
            } else {
                message.error('Failed to update status');
            }
        } catch (e) {
            message.error('Failed to update status');
        }
    };

    // 取消状态变更
    const cancelStatusChange = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
    };

    // 只在设置数据加载完成后才进行第一次数据获取
    React.useEffect(() => {
        if (!settingsLoading && stateOptions.length > 0) {
            doStateFetch({});
        }
    }, [doStateFetch, settingsLoading, stateOptions.length]);

    // Re-fetch only when settings ready (initial) or after mutations handled elsewhere
    // Status filter and sorting are handled on the frontend

    // Ensure moving to first page when search/status/sort changes
    React.useEffect(() => {
        setStatePage(1);
    }, [stateSearchFilter, stateStatusFilter, stateOrderKey, stateOrderType]);

    // Frontend filtering + sorting + pagination on cached list
    React.useEffect(() => {
        const keyword = (stateSearchFilter || '').trim().toLowerCase();
        const statusFilter = stateStatusFilter;
        let filtered = allStateList;
        if (keyword) {
            filtered = filtered.filter((item) => item.state?.toLowerCase().includes(keyword));
        }
        if (statusFilter) {
            filtered = filtered.filter((item) => item.status === statusFilter);
        }
        // Sorting on frontend: currently support LAST_MODIFIED
        if (stateOrderKey === 'LAST_MODIFIED') {
            const asc = stateOrderType === 'ASC';
            filtered = [...filtered].sort((a, b) => {
                const at = a.lastModified ? dayjs(a.lastModified).valueOf() : 0;
                const bt = b.lastModified ? dayjs(b.lastModified).valueOf() : 0;
                return asc ? at - bt : bt - at;
            });
        }
        const startIndex = (statePage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setStateTotal(filtered.length);
        setStateList(filtered.slice(startIndex, endIndex));
    }, [stateSearchFilter, stateStatusFilter, stateOrderKey, stateOrderType, statePage, pageSize, allStateList]);

    // State Management columns
    const stateColumns: ColumnsType<TStateItem> = [
        {
            title: 'STATES',
            dataIndex: 'state',
            key: 'state',
            width: '300px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div
                    className={s.filterDropdown}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <Input
                        placeholder="Search state"
                        value={stateSearchFilter}
                        onChange={(e) => {
                            setStateSearchFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setStatePage(1);
                            close();
                        }}
                        className={s.filterInput}
                    />
                    <Space>
                        <Button
                            onClick={() => {
                                setStateSearchFilter('');
                                setStatePage(1);
                                close();
                            }}
                            size="small"
                            className={s.filterButton}
                        >
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                setStatePage(1);
                                close();
                            }}
                            size="small"
                            className={s.filterButton}
                        >
                            Search
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    className={filtered ? s.filterIconFiltered : undefined}
                />
            ),
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            width: '200px',
            filters: [
                {
                    text: 'Active',
                    value: 'ACTIVE',
                },
                {
                    text: 'Inactive',
                    value: 'INACTIVE',
                },
            ],
            render: (status: string, record: TStateItem) => {
                return (
                    <div className={s.stateStatusContainer}>
                        <Switch
                            checked={status === 'ACTIVE'}
                            onChange={() => handleStateStatusChange(record)}
                            size="small"
                        />
                    </div>
                );
            },
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'lastModified',
            sortDirections: ['ascend', 'descend', 'ascend'],
            sorter: true,
            width: '200px',
            className: s.tableColumn,
            render: (time: string) => {
                return (
                    <div>{time ? dayjs(time).format('MM/DD/YYYY') : 'Nil'}</div>
                );
            },
        },
    ].filter(Boolean) as ColumnsType<TStateItem>;

    return (
        <div className={s.wrap}>
            <CommonTable
                rowKey="id"
                pagination={{
                    total: stateTotal,
                    current: statePage,
                    pageSize,
                    showSizeChanger: false,
                }}
                scroll={{ x: 'max-content' }}
                loading={stateLoading}
                columns={stateColumns}
                onChange={onStateTableChange}
                data={stateList || []}
                className={s.customTable}
            />

            {/* 状态变更确认弹窗 */}
            <Modal
                title={null}
                open={isModalVisible}
                onCancel={cancelStatusChange}
                width={559}
                centered
                className={s.customModal}
                footer={null}
                closeIcon={<span className={s.modalCloseIcon}>✕</span>}
            >
                {/* Header */}
                <div className={s.modalHeader}>
                    <div className={s.modalTitle}>
                        {modalType === 'enable'
                            ? 'Turn on text visits for this state?'
                            : 'Turn off text visits for this state?'
                        }
                    </div>
                </div>

                {/* Content */}
                <div className={s.modalContent}>
                    <p className={s.modalText}>
                        {modalType === 'enable'
                            ? 'Turning on text visits for this state will enable providers in this state to receive text visit cases on the Klarity platform. Are you sure you want to proceed?'
                            : 'Turning off text visits for this state will immediately stop providers in this state from receiving new text visit cases. Existing active cases must still be completed. Are you sure you want to proceed?'
                        }
                    </p>
                </div>

                {/* Footer */}
                <div className={s.modalFooter}>
                    <Button
                        onClick={cancelStatusChange}
                        className={s.modalCancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={confirmStatusChange}
                        className={`${s.modalConfirmButton} ${modalType === 'enable' ? s.enable : s.disable}`}
                    >
                        Confirm
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default StateManagement;
